import React, { useState, useEffect } from "react";
import Template from "../../../components/Template";
import api from "../../../services/api";
import ConfirmationModal from "../../../components/ConfirmationModal";
import SuccessModal from "../../../components/SuccessModal";
import AlertDialog from "../../../components/AlertDialog";
import { useNavigate } from "react-router-dom";
import usePermission from "../../../hooks/usePermission";

const situacoes = ["Ativo", "Inativo"];

const FORM_VAZIO = { nome: "", descricao: "", situacao: "Ativo" };

const GerenciarCategoriaDespesa = () => {
    const navigate = useNavigate();
    const podeGerenciar = usePermission("GERENCIAR_CATEGORIAS_DESPESA");

    const [categorias, setCategorias] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(FORM_VAZIO);
    const [erroNome, setErroNome] = useState("");

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [categoriaParaExcluir, setCategoriaParaExcluir] = useState(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [showAlertDialog, setShowAlertDialog] = useState(false);

    useEffect(() => {
        fetchCategorias();
    }, []);

    const fetchCategorias = async () => {
        setCarregando(true);
        try {
            const { data } = await api.get("/despesas/categorias");
            setCategorias(data);
        } catch (error) {
            setAlertMessage(error.response?.data?.error || "Erro ao carregar as categorias de despesa.");
            setShowAlertDialog(true);
        } finally {
            setCarregando(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (name === "nome" && erroNome) setErroNome("");
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();
        if (!formData.nome.trim()) {
            setErroNome("Informe o nome da categoria.");
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        setSalvando(true);
        try {
            const payload = {
                nome: formData.nome.trim(),
                descricao: formData.descricao,
                situacao: formData.situacao,
            };

            if (isEditMode && editingId) {
                await api.put(`/despesas/categorias/${editingId}`, payload);
            } else {
                await api.post("/despesas/categorias", payload);
            }

            setShowSuccessDialog(true);
            setTimeout(() => setShowSuccessDialog(false), 1500);
            handleCancelEdit();
            fetchCategorias();
        } catch (error) {
            // O 409 de nome repetido chega com a mensagem pronta do backend.
            setAlertMessage(error.response?.data?.error || "Erro ao salvar a categoria.");
            setShowAlertDialog(true);
        } finally {
            setSalvando(false);
        }
    };

    const handleEdit = (categoria) => {
        setIsEditMode(true);
        setEditingId(categoria.id);
        setFormData({
            nome: categoria.nome || "",
            descricao: categoria.descricao || "",
            situacao: categoria.situacao || "Ativo",
        });
        setErroNome("");
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditingId(null);
        setFormData(FORM_VAZIO);
        setErroNome("");
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/despesas/categorias/${categoriaParaExcluir.id}`);
            setShowConfirmDelete(false);
            setCategoriaParaExcluir(null);
            fetchCategorias();
        } catch (error) {
            setShowConfirmDelete(false);
            setCategoriaParaExcluir(null);
            // Categoria em uso volta 409 explicando que o caminho é inativar.
            setAlertMessage(error.response?.data?.error || "Erro ao excluir a categoria.");
            setShowAlertDialog(true);
        }
    };

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-8">
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">Gerenciar Categoria de Despesa</h1>
                    <p className="text-stone-500 text-sm mt-1">
                        As categorias são próprias desta empresa e classificam as contas a pagar.
                    </p>
                </div>

                {podeGerenciar && (
                    <form onSubmit={handleSubmitForm} className="flex flex-col gap-4 bg-white p-4 rounded-md border border-slate-200">
                        <div className="text-stone-600 text-base font-medium">
                            {isEditMode ? "Editar Categoria de Despesa" : "Cadastrar Nova Categoria de Despesa"}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label htmlFor="nome" className="text-sm text-slate-700 font-medium">Nome</label>
                                <input
                                    id="nome"
                                    type="text"
                                    name="nome"
                                    value={formData.nome}
                                    onChange={handleInputChange}
                                    className={`px-3 py-2 border rounded-md text-sm ${erroNome ? "border-red-500" : "border-slate-300"}`}
                                />
                                {erroNome && <span className="text-red-600 text-xs">{erroNome}</span>}
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label htmlFor="situacao" className="text-sm text-slate-700 font-medium">Situação</label>
                                <select
                                    id="situacao"
                                    name="situacao"
                                    value={formData.situacao}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                >
                                    {situacoes.map((sit) => <option key={sit} value={sit}>{sit}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="descricao" className="text-sm text-slate-700 font-medium">Descrição</label>
                            <textarea
                                id="descricao"
                                name="descricao"
                                rows={2}
                                value={formData.descricao}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            />
                        </div>

                        <div className="flex items-center gap-4 mt-4">
                            {isEditMode && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                                >
                                    Cancelar
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={salvando}
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {salvando ? "Salvando..." : (isEditMode ? "Salvar Alterações" : "Adicionar Categoria")}
                            </button>
                        </div>
                    </form>
                )}

                <div className="bg-white p-4 rounded-md border border-slate-200">
                    <div className="text-stone-600 text-base font-medium mb-4">Lista de Categorias de Despesa</div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-stone-100">
                                    <th className="px-4 py-2 border border-slate-200">ID</th>
                                    <th className="px-4 py-2 border border-slate-200">Nome</th>
                                    <th className="px-4 py-2 border border-slate-200">Descrição</th>
                                    <th className="px-4 py-2 border border-slate-200">Situação</th>
                                    {podeGerenciar && <th className="px-4 py-2 border border-slate-200">Ações</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {carregando && (
                                    <tr>
                                        <td colSpan={podeGerenciar ? 5 : 4} className="px-4 py-4 text-center text-slate-500">
                                            Carregando categorias...
                                        </td>
                                    </tr>
                                )}

                                {!carregando && categorias.map((categoria) => (
                                    <tr key={categoria.id} className="border-b">
                                        <td className="px-4 py-2 border border-slate-200">{categoria.id}</td>
                                        <td className="px-4 py-2 border border-slate-200">{categoria.nome}</td>
                                        <td className="px-4 py-2 border border-slate-200">{categoria.descricao || "-"}</td>
                                        <td className="px-4 py-2 border border-slate-200">{categoria.situacao}</td>
                                        {podeGerenciar && (
                                            <td className="px-4 py-2 border border-slate-200">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEdit(categoria)}
                                                    className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2"
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setCategoriaParaExcluir(categoria); setShowConfirmDelete(true); }}
                                                    className="px-2 py-1 bg-red-600 text-white rounded-md text-sm"
                                                >
                                                    Excluir
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}

                                {!carregando && categorias.length === 0 && (
                                    <tr>
                                        <td colSpan={podeGerenciar ? 5 : 4} className="px-4 py-4 text-center text-slate-500">
                                            Nenhuma categoria de despesa cadastrada.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="flex items-center w-full gap-4">
                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={() => navigate('/configuracoes/menus')}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="" />
                            <span>Voltar</span>
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirmSubmit}
                onCancel={() => setShowConfirmDialog(false)}
                message="Deseja salvar este registro?"
            />

            <ConfirmationModal
                isVisible={showConfirmDelete}
                onConfirm={handleConfirmDelete}
                onCancel={() => { setShowConfirmDelete(false); setCategoriaParaExcluir(null); }}
                message={`Deseja apagar a categoria "${categoriaParaExcluir?.nome}"?`}
                textButtonConfirm="Sim"
            />

            <SuccessModal
                isVisible={showSuccessDialog}
                message="Registro salvo com sucesso!"
                onClose={() => setShowSuccessDialog(false)}
            />

            {showAlertDialog && (
                <AlertDialog
                    isVisible={showAlertDialog}
                    message={alertMessage}
                    onClose={() => setShowAlertDialog(false)}
                />
            )}
        </Template>
    );
};

export default GerenciarCategoriaDespesa;
